import React, { useCallback, useState } from 'react';
import { Grid, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import useDeviceResize from '../../../../hooks/useDeviceResize';
import Currency from '../../../../components/Currency';
import IncludedInsuranceList from '../../../../components/MUI/SummaryWrapper/IncludedInsuranceList';
import ArrowDropIcon from '../../../../components/MUI/Icons/ArrowDropIcon';
import TitleTypography from '../../../../components/MUI/TitleTypography';

import useStyles from '../../../../components/MUI/SummaryWrapper/Details/makeStyles';
import useSummaryWrapperStyles from '../../../../components/MUI/SummaryWrapper/makeStyles';

const ContractDetailsSection = ({
  selectedLimit,
  selectedPlan,
  range,
  region,
  days,
  activeCoveragesCount,
  handleOpenAdditionalCoveragesModal,
  selectedPlansWithAllowedLimitRegionAndAge,
}) => {
  const { t } = useTranslation();
  const isDeviceLgSize = useDeviceResize('lg');
  const [showCoverages, setOpenCoverages] = useState(false);
  const classes = useStyles();
  const summaryWrapperClasses = useSummaryWrapperStyles();

  const handleOpenCoverages = useCallback(() => {
    if (!isEmpty(selectedPlansWithAllowedLimitRegionAndAge)) {
      if (!isDeviceLgSize) {
        handleOpenAdditionalCoveragesModal();
      }
      if (isDeviceLgSize) {
        setOpenCoverages(!showCoverages);
      }
    }
  }, [
    showCoverages,
    handleOpenAdditionalCoveragesModal,
    isDeviceLgSize,
    selectedPlansWithAllowedLimitRegionAndAge,
  ]);

  return (
    <>
      <Grid container spacing={2} direction="row">
        <Grid item xs={8} className={classes.label}>
          <Typography>{t('contract.summaryFields.type')}</Typography>
        </Grid>
        <Grid item xs={4} className={classes.valueContainer}>
          <div
            className={classes.activeElitePlan}
            role="presentation"
            onClick={handleOpenCoverages}
          >
            {!isEmpty(selectedPlan) ? (
              <div className={classes.centeredDiv}>
                {t(`travel.types.${selectedPlan}.title`)}
                {isDeviceLgSize && <ArrowDropIcon up={showCoverages} />}
              </div>
            ) : (
              '-'
            )}
          </div>
        </Grid>
      </Grid>
      {!!Object.keys(selectedPlansWithAllowedLimitRegionAndAge).length && showCoverages && (
        <Grid item xs={12}>
          <Grid item className={classes.includedInsuranceSection}>
            <Grid container spacing={2} direction="row">
              <Grid item xs={12}>
                <TitleTypography component="div" className={summaryWrapperClasses.sectionTitle}>
                  {t('insurance.includedInsurance')}
                </TitleTypography>
              </Grid>
            </Grid>

            <Grid container spacing={2} direction="row">
              <Grid item xs={12}>
                <IncludedInsuranceList plan={selectedPlansWithAllowedLimitRegionAndAge} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid container spacing={2} direction="row">
        <Grid item xs={8} className={classes.label}>
          <Typography>{t('travel.additionalCoverages')}</Typography>
        </Grid>
        <Grid item xs={4} className={classes.valueContainer}>
          {activeCoveragesCount}
        </Grid>
      </Grid>
      <Grid container spacing={2} direction="row">
        <Grid item xs={8} className={classes.label}>
          <Typography>{t('contract.summaryFields.coverage')}</Typography>
        </Grid>
        <Grid item xs={4} className={classes.valueContainer}>
          {selectedLimit.value && !isEmpty(selectedPlan) ? (
            <div className={classes.centeredDiv}>
              <Currency value={selectedLimit.value} currency={selectedLimit.currency} withSymbol />
            </div>
          ) : (
            <div>-</div>
          )}
        </Grid>
      </Grid>
      <Grid container spacing={2} direction="row">
        <Grid item xs={8} className={classes.label}>
          <Typography>{t('contract.summaryFields.startDate')}</Typography>
        </Grid>
        <Grid item xs={4} className={classes.valueContainer}>
          {/* eslint-disable-next-line no-restricted-globals */}
          <div>{range.start && !isNaN(range.start) ? format(range.start, 'dd/MM/yyyy') : '-'}</div>
        </Grid>
      </Grid>
      <Grid container spacing={2} direction="row">
        <Grid item xs={8} className={classes.label}>
          <Typography>{t('contract.summaryFields.endDate')}</Typography>
        </Grid>
        <Grid item xs={4} className={classes.valueContainer}>
          {/* eslint-disable-next-line no-restricted-globals */}
          <div>{range.end && !isNaN(range.end) ? format(range.end, 'dd/MM/yyyy') : '-'}</div>
        </Grid>
      </Grid>
      <Grid container spacing={2} direction="row">
        <Grid item xs={8} className={classes.label}>
          <Typography>{t('contract.summaryFields.days')}</Typography>
        </Grid>
        <Grid item xs={4} className={classes.valueContainer}>
          <div>{days || '-'}</div>
        </Grid>
      </Grid>
      <Grid container spacing={2} direction="row">
        <Grid item xs={8} className={classes.label}>
          <Typography>{t('contract.summaryFields.region')}</Typography>
        </Grid>
        <Grid item xs={4} className={classes.valueContainer}>
          <div>{!isEmpty(region) ? t(`travel.region.${region}.title`) : '-'}</div>
        </Grid>
      </Grid>
    </>
  );
};

ContractDetailsSection.propTypes = {
  selectedLimit: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]).isRequired,
  selectedPlan: PropTypes.string.isRequired,
  range: PropTypes.shape({
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date),
  }).isRequired,
  days: PropTypes.number.isRequired,
  region: PropTypes.string.isRequired,
  activeCoveragesCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleOpenAdditionalCoveragesModal: PropTypes.func.isRequired,
  selectedPlansWithAllowedLimitRegionAndAge: PropTypes.shape({}).isRequired,
};

export default ContractDetailsSection;
